/**
 * What to do with what is already on the floor when an import arrives.
 *
 * The old question was "overwrite — yes or no?", and the honest answer was
 * usually "partly". A new drawing is authoritative about walls and rooms; the
 * portal is authoritative about everything a drawing cannot express — categories,
 * person exposure, the assets someone walked the building to record. Forcing one
 * answer for all of it means losing one of the two.
 *
 * So the dialog asks per object kind, and every switch is off by default:
 * merging keeps data, overwriting destroys it, and the safe direction should not
 * require vigilance. The floorplan image is the exception and starts on — a new
 * plan of the same rooms is the ordinary case, and keeping the old picture next
 * to new outlines would be the confusing outcome.
 */

import { useId } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export interface ImportOverwrite {
  floors: boolean;
  spaces: boolean;
  doors: boolean;
  assets: boolean;
  floorplanImage: boolean;
}

interface Properties {
  open: boolean;
  spaces: number;
  assets: number;
  overwrite: ImportOverwrite;
  onChange: (value: ImportOverwrite) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const KINDS: { key: keyof ImportOverwrite; labelKey: string; hintKey: string }[] = [
  { key: "floorplanImage", labelKey: "importMerge.floorplanImage", hintKey: "importMerge.floorplanImageHint" },
  { key: "spaces", labelKey: "importMerge.spaces", hintKey: "importMerge.spacesHint" },
  { key: "doors", labelKey: "importMerge.doors", hintKey: "importMerge.doorsHint" },
  { key: "assets", labelKey: "importMerge.assets", hintKey: "importMerge.assetsHint" },
  { key: "floors", labelKey: "importMerge.floors", hintKey: "importMerge.floorsHint" },
];

export default function ImportMergeDialog(properties: Properties) {
  const { t } = useTranslation();
  const fieldId = useId();

  return (
    <Dialog
      open={properties.open}
      onOpenChange={(next) => {
        if (!next) properties.onCancel();
      }}
    >
      {/* 560px wie im Bestand, auf schmalen Viewports aber innerhalb des
          Bildschirms statt darüber hinaus. */}
      <DialogContent className="max-w-none sm:w-[560px] w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{t("importMerge.title")}</DialogTitle>
          <DialogDescription>
            {t("importMerge.subtitle", {
              spaces: properties.spaces,
              assets: properties.assets,
            })}
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{t("importMerge.explanation")}</p>

        <div className="flex flex-col gap-4">
          {KINDS.map((kind) => (
            <div key={kind.key} className="flex min-w-0 items-start gap-2">
              <Checkbox
                id={`${fieldId}-${kind.key}`}
                className="mt-0.5"
                checked={properties.overwrite[kind.key]}
                onCheckedChange={(checked) =>
                  properties.onChange({ ...properties.overwrite, [kind.key]: checked === true })
                }
              />
              <div className="min-w-0">
                <Label htmlFor={`${fieldId}-${kind.key}`} className="break-words">
                  {t(kind.labelKey)}
                </Label>
                <p className="mt-1 break-words text-xs text-muted-foreground">{t(kind.hintKey)}</p>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={properties.onCancel}>
            {t("global.cancel")}
          </Button>
          <Button type="button" onClick={properties.onConfirm}>
            {t("importMerge.start")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
